import React, { useEffect, useRef, useState } from "react";

/**
 * Lazy image with a reveal the viewer can actually SEE.
 *
 * The previous version had one observer with a 300px head-start: images
 * loaded early (good) but also revealed early (bad) -- the animation played
 * off-screen, so scrolling felt dead. Loading and revealing are now split:
 *
 *   LOAD    fires 400px ahead      -> network starts early, no waiting
 *   REVEAL  fires ~8% into view    -> the fade+rise happens in front of you
 *
 * The entrance is a CSS animation (not a transition) delayed by
 * --reveal-delay, which lets a grid cascade row by row. When it finishes,
 * the img settles to a plain class so hover effects work normally.
 * prefers-reduced-motion collapses the animation globally.
 */
export default function LazyImage({
  src,
  alt = "",
  aspect,
  className = "",
  eager = false,
  revealDelay = 0,
}) {
  const ref = useRef(null);
  const [near, setNear] = useState(eager); // start loading
  const [loaded, setLoaded] = useState(false);
  const [revealed, setRevealed] = useState(false); // actually on screen
  const [settled, setSettled] = useState(false); // entrance finished

  // LOAD observer -- generous head start.
  useEffect(() => {
    if (near) return;
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [near]);

  // REVEAL observer -- only when genuinely visible.
  useEffect(() => {
    if (revealed) return;
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setRevealed(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [revealed]);

  const playing = loaded && revealed && !settled;

  return (
    <div
      ref={ref}
      className={`lazy-frame ${className}`}
      style={aspect ? { aspectRatio: aspect } : undefined}
    >
      {near && (
        <img
          src={src}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          className={settled ? "is-settled" : playing ? "is-in" : ""}
          style={{
            "--reveal-delay": `${revealDelay}ms`,
            ...(aspect ? {} : { height: "auto" }),
          }}
          onLoad={() => setLoaded(true)}
          onAnimationEnd={() => setSettled(true)}
        />
      )}
    </div>
  );
}